import { useState, useEffect } from 'react';
import { X, User, Mail, Crown, Check, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AccountDataModal = ({ isOpen, onClose, user }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
  });
  const [loading, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
      });
    }
  }, [user]);

  // Track changes
  useEffect(() => {
    if (user) {
      const changed = formData.name !== user.name || formData.email !== user.email;
      setHasChanges(changed);
    }
  }, [formData, user]);

  // Get plan display info
  const getPlanInfo = () => {
    const planType = user?.plan_type || 'free';
    switch (planType) {
      case 'basic':
        return { name: 'Basic', price: '4.99 €/lună', color: 'text-blue-600', bg: 'bg-blue-50' };
      case 'pro':
        return { name: 'Pro', price: '9.99 €/lună', color: 'text-gold', bg: 'bg-gold/10' };
      case 'premium':
        return { name: 'Premium', price: '29.99 €/lună', color: 'text-purple-600', bg: 'bg-purple-50' };
      default:
        return { name: 'Free', price: 'Gratuit', color: 'text-gray-600', bg: 'bg-gray-100' };
    }
  };

  const handleSave = async () => {
    if (!hasChanges) return;
    
    setSaving(true);
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Eroare la salvare');
      }

      const result = await response.json();
      
      // Update localStorage
      const updatedUser = { ...user, ...result.user };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      // Dispatch event for Navbar update
      window.dispatchEvent(new CustomEvent('user-login', { detail: updatedUser }));
      
      toast.success('Datele au fost salvate cu succes!');
      onClose();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const planInfo = getPlanInfo();

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    // Only close if clicking directly on the backdrop, not on children
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <>
      {/* Backdrop - fixed to viewport */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        style={{ zIndex: 9998 }}
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Modal container - using grid for perfect centering */}
      <div 
        className="fixed grid place-items-center p-4"
        style={{ 
          zIndex: 9999, 
          pointerEvents: 'none',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh'
        }}
      >
        {/* Modal */}
        <div 
          className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[85vh] overflow-y-auto"
          style={{ pointerEvents: 'auto' }}
          data-testid="account-data-modal"
          onClick={(e) => e.stopPropagation()}
        >
        {/* Header */}
        <div 
          className="px-6 py-4 border-b border-gray-100"
          style={{ background: 'linear-gradient(135deg, #0A2540 0%, #1a3a5c 100%)' }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)' }}
              >
                <User className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">Datele Contului</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-all"
              data-testid="close-account-data-modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Name Field */}
          <div>
            <label className="block text-sm font-semibold text-navy mb-2">
              Nume complet
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all text-navy"
                placeholder="Numele tău"
                data-testid="profile-name-input"
              />
            </div>
          </div>

          {/* Email Field */}
          <div>
            <label className="block text-sm font-semibold text-navy mb-2">
              Adresa de e-mail
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all text-navy"
                placeholder="email@exemplu.com"
                data-testid="profile-email-input"
              />
            </div>
          </div>

          {/* Current Plan Display */}
          <div>
            <label className="block text-sm font-semibold text-navy mb-2">
              Planul curent
            </label>
            <div className={`flex items-center justify-between p-4 rounded-xl ${planInfo.bg}`}>
              <div className="flex items-center gap-3">
                <Crown className={`w-5 h-5 ${planInfo.color}`} />
                <div>
                  <p className={`font-bold ${planInfo.color}`}>{planInfo.name}</p>
                  <p className="text-sm text-gray-500">{planInfo.price}</p>
                </div>
              </div>
              {user?.plan_type !== 'premium' && (
                <Button
                  onClick={() => window.location.href = '/#ai-chat'}
                  className="text-sm px-4 py-2"
                  style={{ 
                    background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
                    color: 'white'
                  }}
                  data-testid="upgrade-plan-btn"
                >
                  Upgrade
                </Button>
              )}
            </div>
          </div>

          {/* Admin Badge */}
          {user?.is_super_admin && (
            <div className="flex items-center gap-2 p-3 bg-gold/10 rounded-xl">
              <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                <Crown className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="font-semibold text-navy text-sm">Administrator</p>
                <p className="text-xs text-gray-500">Ai acces la Dashboard Admin</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
          <Button
            onClick={onClose}
            variant="outline"
            className="px-6 border-navy text-navy hover:bg-navy/5"
            data-testid="cancel-account-data-btn"
          >
            Anulează
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || loading}
            className="px-6"
            style={{ 
              background: hasChanges ? 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)' : '#ccc',
              color: 'white',
              opacity: hasChanges ? 1 : 0.6
            }}
            data-testid="save-profile-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Check className="w-4 h-4 mr-2" />
            )}
            Salvează modificările
          </Button>
        </div>
      </div>
      </div>
    </>
  );
};
